import { _ } from "../core/polyglot/polyglot.js";
import { isValidEmailAddress } from "../core/helpers/string-util.js";
import Company from "./Company.js";
import Model from "./Model.js";

/**
 * Base user model. This model should not be used as an instance object in it self, it
 * should only be, sub-classed (see companyUser and agencyUser).
 *
 * @param {object} spec - Model configuration
 * @param {string} spec.id - User id (same as user name)
 * @param {string} spec.name - User's full name (e.g John Doe)
 * @param {string} spec.phone - Phone number
 * @param {string} spec.cellphone - Phone number
 * @param {string} spec.email - email address of the user
 * @param {string} spec.creationDate - Date string
 * @param {string} spec.language - Locale
 * @param {string} spec.typeOfUser - "Presentation", "AgencyConsultant", "AgencyDirector" or "BackOffice"
 * @param {string} [spec.avatarURL] - Url to the user avatar
 * @param {string} [spec.analyticsId] - User id for analytics (We need to anonymize the user)
 * @param {boolean} [spec.isTemporaryUser] - boolean that indicates if user is not persisted in our data store e.g. SSO users
 * @param {boolean} [spec.hasPendingInvitation] - boolean that indicates if user has a pending invitation
 * @param {boolean} [spec.isInAaa] - boolean that indicates if user has a connected Wolters Kluwer account
 * @param {boolean} [spec.canViewReleaseNotification] - Whether the
 *   user has access to release notes
 * @param {object} my - Protected properties
 * @return {user}
 */
export default class User extends Model {
	constructor(spec = {}) {
		super(spec);
		this._id = spec.id;
		this._name = spec.name || "";
		this._phone = spec.phone;
		this._cellphone = spec.cellphone;
		this._email = spec.email;
		this._creationDate = spec.creationDate;
		this._language = spec.language;
		this._typeOfUser = spec.typeOfUser || this._defaultTypeOfUser();
		this._avatarUrl = spec.avatarURL || this._defaultAvatarUrl();
		this._analyticsId = spec.analyticsId;
		this._isTemporaryUser = spec.isTemporaryUser;
		this._hasPendingInvitation = spec.hasPendingInvitation;
		this._isInAaa = spec.isInAaa;
		this._canViewReleaseNotification = spec.canViewReleaseNotification;
	}

	getId() {
		return this._id;
	}

	getBusinessIdentityCodes() {
		return this.getCompanyIds().map(
			Company.convertCompanyIdToBusinessIdentityCode,
		);
	}

	canBeNotifiedForEntries(entries, company) {
		return entries.some((entry) => this.canBeNotifiedFor(entry, company));
	}

	canBeNotifiedFor(_entry, _company) {
		return false;
	}

	canForcePackageSelection() {
		return false;
	}

	setId(id) {
		if (this._id) {
			throw new Error("Can not update user name");
		}
		this._id = id;
	}

	getName() {
		return this._name;
	}

	setName(newName) {
		this._name = newName;
	}

	getPhone() {
		return this._phone;
	}

	setPhone(newPhone) {
		this._phone = newPhone;
	}

	getCellphone() {
		return this._cellphone;
	}

	setCellphone(newCellphone) {
		this._cellphone = newCellphone;
	}

	getEmail() {
		return this._email;
	}

	setEmail(newEmail) {
		this._email = newEmail;
	}

	getCreationDate() {
		return this._creationDate;
	}

	getLanguage() {
		return this._language;
	}

	setLanguage(newLanguage) {
		this._language = newLanguage;
	}

	getTypeOfUser() {
		return this._typeOfUser;
	}

	getUsername() {
		return this.getId();
	}

	getAnalyticsId() {
		return this._analyticsId;
	}

	hasValidEmail() {
		return isValidEmailAddress(this._email);
	}

	isUnknownUser() {
		return false;
	}

	isBackOfficeUser() {
		return false;
	}

	isAgencyUser() {
		return false;
	}

	isAgencyDirector() {
		return false;
	}

	isConsultant() {
		return false;
	}

	isCompanyUser() {
		return false;
	}

	isTemporaryUser() {
		return this._isTemporaryUser;
	}

	isCountryUser() {
		return false;
	}

	hasPendingInvitation() {
		return this._hasPendingInvitation;
	}

	isInAaa() {
		return this._isInAaa;
	}

	// Permissions

	canManagePackages() {
		return this.getRoles().some((role) => role.canManagePackages());
	}

	canAdministrateAgency(agency) {
		return this.getRoles().some((role) =>
			role.canAdministrateAgency(agency),
		);
	}

	canAdministrateDocumentArchive() {
		return this.getRoles().some((role) =>
			role.canAdministrateDocumentArchive(),
		);
	}

	canEditLightCompanies() {
		return this.getRoles().some((role) => role.canEditLightCompanies());
	}

	canDeleteUser(user) {
		return (
			this.getId() !== user.getId() &&
			this.getRoles().some((role) => role.canDeleteUser(user))
		);
	}

	getRoles() {
		return this._subclassResponsibility();
	}

	canEditPresetsFor(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canEditKeyFigureDataFor(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canEditKeyFiguresFor(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canDeleteKeyFiguresFor(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canImportTo(company) {
		return this.isImportUser(company.getLegacyId());
	}

	canManageUsersFor(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canCreatePeriodReports(company) {
		return this.getRoles().some((role) =>
			role.canCreatePeriodReports(company),
		);
	}

	canManageReportTemplates(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canViewWithdrawnPeriodReports(_company) {
		return false;
	}

	canSeePeriodReportViewers(_company) {
		return false;
	}

	canRegisterViewEventForReport() {
		return false;
	}

	canSendPeriodNotifications(_company) {
		return false;
	}

	canEditCompanyInfoFor(company) {
		return (
			this.isAdminUser(company.getLegacyId()) ||
			this.isImportUser(company.getLegacyId())
		);
	}

	canEditPreliminaryDocuments() {
		return (
			this.isBackOfficeUser() ||
			this.isAgencyUser() ||
			this.isConsultant() ||
			this.isCountryUser()
		);
	}

	canAdministrateForecastsFor(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canEditChartOfAccountFor(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canEditBudgetFor(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canDeleteAgencyChartOfAccounts() {
		return this.canShareChartOfAccounts();
	}

	canShareChartOfAccounts() {
		return this.getRoles().some((role) => role.canShareChartOfAccounts());
	}

	canSeeInactivePeriod(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canViewAccountingDocuments(company) {
		return this.getRoles().some((role) =>
			role.canViewAccountingDocuments(company),
		);
	}

	canViewDocumentArchiveDocument(company) {
		// viewing document archive and accounting documents is currently the same permission:
		return this.canViewAccountingDocuments(company);
	}

	canViewCompanySettingsDocument(company) {
		// viewing company settings and accounting documents is currently the same permission:
		return this.canViewAccountingDocuments(company);
	}

	canViewBudgetEditor(company) {
		return this.getRoles().some((role) =>
			role.canViewBudgetEditor(company),
		);
	}

	canSaveForecastToBudget(company) {
		return this.getRoles().some((role) =>
			role.canSaveForecastToBudget(company),
		);
	}

	canEditUserInfo(user) {
		return (
			this.getId() === user.getId() ||
			this.getRoles().some((role) => role.canEditUserInfo(user))
		);
	}

	canEditCompanyRoleFor(user) {
		return this.canEditUserInfo(user);
	}

	canExtractDataFromCompany(company) {
		if (company.isProvisionedByOneClick()) return false;

		return this.getRoles().some((role) =>
			role.canExtractDataFromCompany(company),
		);
	}

	canDeleteCompany(company) {
		return this.getRoles().some((role) => role.canDeleteCompany(company));
	}

	canArchiveCompany() {
		return (
			this.isBackOfficeUser() ||
			this.isCountryUser() ||
			this.isAgencyDirector()
		);
	}

	canUpgradeCompany(company) {
		return this.getRoles().some((role) =>
			role.canUpgradeSubscriptionPlanForCompany(company),
		);
	}

	canDowngradeCompany(_company) {
		return false;
	}

	// demotion is only from director to consultant
	canDemote(user) {
		// TODO FTGP-5674: use user roles.
		//
		// This is far from good but is ok for now as:
		//
		// - this is better than what is in use today
		// - this only affects Agency2
		return (
			this.getId() !== user.getId() &&
			(this.isAgencyDirector() ||
				this.isBackOfficeUser() ||
				this.isCountryUser()) &&
			user.isAgencyDirector()
		);
	}

	// promotion is only from consultant to director
	canPromote(user) {
		// TODO FTGP-5673: use user roles.
		//
		// This is far from good but is ok for now as:
		//
		// - this is better than what is in use today
		// - this only affects Agency2
		return (
			this.getId() !== user.getId() &&
			(this.isAgencyDirector() ||
				this.isBackOfficeUser() ||
				this.isCountryUser()) &&
			user.isConsultant()
		);
	}

	canAttachCompany(user, company) {
		return this.canAttachCompanyId(user, company.getLegacyId());
	}

	canAttachCompanyId(user, companyId) {
		return (
			!user.isAttachedToCompanyId(companyId) &&
			// canDetach and canAttach are equivalent authorizations
			this.getRoles().some((role) =>
				role.canAttachOrDetachCompanyId(user, companyId),
			)
		);
	}

	canDetachCompany(user, company) {
		return this.canDetachCompanyId(user, company.getLegacyId());
	}

	canDetachCompanyId(user, companyId) {
		return (
			user.isAttachedToCompanyId(companyId) &&
			// Ugly but will get fixed by FTGP-5715
			(!user.isCompanyUser() || user.getCompanyIds().length > 1) &&
			this.getRoles().some((role) =>
				role.canAttachOrDetachCompanyId(user, companyId),
			)
		);
	}

	canChangeCompanyPackage(company) {
		return this.getRoles().some((role) =>
			role.canChangeCompanyPackage(company),
		);
	}

	canEditLiquidityReport(_company) {
		return false;
	}

	canGoToCountry() {
		return false;
	}

	canViewReleaseNotification() {
		return this._canViewReleaseNotification;
	}

	isAttachedToCompanyId(companyId) {
		return this.getCompanyIds && this.getCompanyIds().includes(companyId);
	}

	/**
	 * Answer true if the user has the role among `roles`. The
	 * default implementation returns `false` unless `roles`
	 * is undefined or an empty array.
	 *
	 * @param {object[]} roles - Array of roles.
	 */
	hasCompanyRole(roles) {
		return !roles || !roles.length;
	}

	isAdminUser(companyId) {
		return this.hasCompanyRole(["admin"], companyId);
	}

	isImportUser(companyId) {
		return this.hasCompanyRole(["import"], companyId);
	}

	isNormalUser(companyId) {
		return this.hasCompanyRole(["normal"], companyId);
	}

	isLimitedUser(companyId) {
		return this.hasCompanyRole(["limited"], companyId);
	}

	isDocumentArchiveUser(companyId) {
		return this.hasCompanyRole(["document-archive"], companyId);
	}

	getUserTypePresentationName() {
		return this.constructor.getUserTypePresentationName(this._typeOfUser);
	}

	/**
	 * Returns a presentation friendly name of the user.
	 *
	 * @return {string} Full name and user name
	 */
	toString() {
		return `${this._name} (${this._id})`;
	}

	getPictureUrl() {
		return this._avatarUrl;
	}

	useDefaultPictureUrl() {
		this._avatarUrl = this._defaultAvatarUrl();
	}

	useStoredPictureUrl() {
		this._avatarUrl = `/Shared/Avatar/User/${this.getUsername()}`;
	}

	isUsingDefaultPictureUrl() {
		return this.getPictureUrl() === this._defaultAvatarUrl();
	}

	_defaultAvatarUrl() {
		return "/Client/img/avatar-user-grey-lg@2x.png";
	}

	_defaultTypeOfUser() {
		return "Undefined";
	}

	_getMatchingFields() {
		return [this.getName(), this.getEmail()];
	}

	_serializedProperties() {
		const schema = super._serializedProperties();

		// Accepted keys server-side from UserPatch
		const authorizedKeys = [
			"name",
			"phone",
			"email",
			"cellphone",
			"language",
		];

		authorizedKeys.forEach((key) => {
			if (!this[`_${key}`]) {
				return;
			}
			schema[key] = this[`_${key}`];
		});

		// Takes care of name mismatch between server and client:
		schema.username = this._id;

		return schema;
	}

	static getUserTypePresentationName(typeOfUser) {
		switch (typeOfUser) {
			case "Presentation":
			case "CompanyUser": {
				return _("Company user");
			}
			case "AgencyConsultant": {
				return _("Agency consultant");
			}
			case "AgencyDirector": {
				return _("Agency director");
			}
			case "CountryUser": {
				return _("Country user");
			}
			case "BackOffice": {
				return _("Back office user");
			}
			default: {
				return _("n/a");
			}
		}
	}

	static possibleTypes() {
		return [];
	}

	static matchesType(type) {
		return this.possibleTypes().indexOf(type) !== -1;
	}

	static transformMaterializationData(data, _context) {
		if (!data.hasAvatar) {
			return;
		}
		data.avatarURL = `/Shared/Avatar/User/${data.username}`;
	}
}
